/**
 * EditMilestoneDialog Component
 *
 * Dialog for editing milestone details (title, dates, assignee)
 */

import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import { Edit } from '@mui/icons-material';
import { useOKRDashboardContext } from '../OKRDashboardContext';
import { okrApi } from '../../../services/okrApi';
import { COMPLETION_STATUS_OPTIONS, getStatusColor } from '../utils/scoreUtils';

export const EditMilestoneDialog: React.FC = () => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { data, dialogs } = useOKRDashboardContext();
    const { setSuccess, setError, softRefetchData, employees, userContext } = data;
    const {
        dialogs: dialogState,
        formState,
        updateFormField,
        closeEditMilestoneDialog,
    } = dialogs;

    const milestone = formState.selectedMilestoneForEdit;

    // Local state for form fields
    const [title, setTitle] = useState('');
    const [startDate, setStartDate] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [assignedTo, setAssignedTo] = useState('');
    const [status, setStatus] = useState('not_started');

    // Initialize form when dialog opens
    useEffect(() => {
        if (milestone) {
            setTitle(milestone.title || '');
            setStartDate(milestone.start_date ? milestone.start_date.split('T')[0] : '');
            setDueDate(milestone.due_date ? milestone.due_date.split('T')[0] : '');
            setAssignedTo(milestone.assigned_to || '');
            setStatus(milestone.status || 'not_started');
        }
    }, [milestone]);

    const handleSubmit = async () => {
        if (!milestone?.id || !title.trim()) return;

        try {
            await okrApi.updateMilestone(
                milestone.id,
                title.trim(),
                startDate || undefined,
                dueDate || undefined,
                assignedTo || undefined,
                status
            );
            setSuccess('Milestone updated successfully');
            if (softRefetchData) {
                await softRefetchData();
            }
            closeEditMilestoneDialog();
        } catch (err: any) {
            setError(err.message || 'Failed to update milestone');
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && e.ctrlKey) {
            handleSubmit();
        }
    };

    const handleClose = () => {
        closeEditMilestoneDialog();
        setTitle('');
        setStartDate('');
        setDueDate('');
        setAssignedTo('');
        setStatus('not_started');
    };

    return (
        <Dialog
            open={dialogState.editMilestoneDialog}
            onClose={handleClose}
            fullScreen={isMobile}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: isDarkMode ? '#1e293b' : '#fff',
                    borderRadius: isMobile ? 0 : 3,
                    border: '1px solid',
                    borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                    minHeight: isMobile ? '100vh' : 520,
                    maxHeight: isMobile ? '100vh' : '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    pb: 2,
                    flexShrink: 0,
                }}
            >
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Edit sx={{ color: '#fff', fontSize: 20 }} />
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                    Edit Milestone
                </Typography>
            </DialogTitle>

            <DialogContent dividers sx={{ pt: 3, pb: 2, overflowY: 'auto' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
                    <TextField
                        label="Milestone Title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        onKeyDown={handleKeyDown}
                        fullWidth
                        required
                        autoFocus
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                            },
                        }}
                    />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            label="Start Date"
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                                },
                            }}
                        />
                        <TextField
                            label="Due Date"
                            type="date"
                            value={dueDate}
                            onChange={(e) => setDueDate(e.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                                },
                            }}
                        />
                    </Box>
                    <FormControl fullWidth>
                        <InputLabel>Assigned To</InputLabel>
                        <Select
                            value={assignedTo}
                            onChange={(e) => setAssignedTo(e.target.value)}
                            label="Assigned To"
                            sx={{
                                bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                            }}
                        >
                            <MenuItem value="">
                                <em>Unassigned</em>
                            </MenuItem>
                            {userContext?.id && (
                                <MenuItem value={userContext.id}>
                                    <strong>Assign to me</strong>
                                </MenuItem>
                            )}
                            {employees?.filter((emp: any) =>
                                (emp.emp_id || emp.id) !== userContext?.id &&
                                Number(emp.dept_id || emp.department_id) === Number(userContext?.dept_id)
                            ).map((emp: any) => (
                                <MenuItem key={emp.emp_id || emp.id} value={emp.emp_id || emp.id}>
                                    {emp.name || `${emp.first_name} ${emp.last_name}`}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            label="Status"
                            sx={{
                                bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                                color: getStatusColor(status),
                                fontWeight: 600,
                            }}
                        >
                            {COMPLETION_STATUS_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value} sx={{ color: opt.color }}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Typography
                        variant="caption"
                        sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}
                    >
                        Tip: Press Ctrl+Enter to save quickly
                    </Typography>
                </Box>
            </DialogContent>

            <DialogActions
                sx={{
                    px: 3,
                    py: 2,
                    borderTop: '1px solid',
                    borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                }}
            >
                <Button
                    onClick={handleClose}
                    sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!title.trim()}
                    sx={{
                        background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                        fontWeight: 600,
                        px: 3,
                        '&:hover': {
                            background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
                        },
                    }}
                >
                    Save Changes
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditMilestoneDialog;
